/**
 * Doctors Component
 *
 * Description: This component displays a list of doctors with functionalities to filter, approve, decline, and view details.
 *
 * File Name: Doctors.tsx
 * Date: [Current Date]
 */

import React, { FormEvent, useEffect, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import BreadCrumb from "src/Components/GlobalComponents/BreadCrumb";
import SearchFilterInput from "src/Components/GlobalComponents/SearchFilterInput";
import TableWrapper from "src/Components/GlobalComponents/TableWrapper";
import ToggleRadioBtn from "src/Components/ToggleRadioBtn";
import { IDoctorItems, IqueryParams } from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import eyeIcon from "src/assets/images/view.png";
import viewTest from "src/assets/images/view-test.png";
import addTest from "src/assets/images/all-test.png";
import httpRequest from "src/helpers/httpRequest";
import qs from "qs";
import { toast } from "react-toastify";
import { CircularProgress, Modal } from "@mui/material";
import DeclineModalReason from "src/Components/AdminComponents/DeclineModalReason";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import { CHECKBOX_INPUT } from "src/lib/constans";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import SimpleInput from "src/Components/SimpleInput";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/Store/store";
import { updateIndicators, getIndicators } from "src/Store/redDots";
import { canWrite } from "src/helpers/helperFunctions";

interface IPayloadState {
  subject: string;
  title: string;
  descripiton: string;
}
const initialState: IPayloadState = {
  subject: "",
  title: "",
  descripiton: "",
};
const initialErroState = {
  subject: "",
  title: "",
  descripiton: "",
};

interface IDoctorResponse {
  documents: IDoctorItems[];
  paginated: IPaginated;
}

const Doctors: React.FC = () => {
  // Style for the declined button
  const redBtn: React.CSSProperties = {
    padding: "4px 12px",
    border: "1px solid red",
    color: "red",
    borderRadius: 4,
  };

  const greenBtn: React.CSSProperties = {
    padding: "4px 12px",
    border: "1px solid #00443f",
    color: "#00443f",
    borderRadius: 4,
    minWidth: 60,
  };

  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "userManagement", false);
  const { allIndicators } = useSelector((store: RootState) => store?.redDots);

  // List of tab names for filtering orders
  const tabList: string[] = ["approved", "pending"];

  // loader states
  const [approvedId, setApprovedId] = useState<string>("");
  const [statusId, setStatusId] = useState<string>("");
  const [isLoader, setIsLoader] = useState<boolean>(false);
  // loader states

  // decline with reason
  const [declineId, setDeclineId] = useState<string>("");
  const [declineLoader, setDeclineLoader] = useState<boolean>(Boolean);
  const [message, setMessage] = useState<string>("");
  // decline with reason

  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination
  const [allDoctors, setAllDoctors] = useState<IDoctorItems[]>([]); // Stores the list of doctors
  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
    status: "approved",
    search: "",
    count: false,
  }); // Query parameters for API requests

  // Updates search text in query parameters
  const handleChangeText = (txt: string) => {
    setQueryParams((prev) => ({
      ...prev,
      search: txt,
      page: 1,
    }));
  };

  // Handles page change in pagination
  const handlePageChange = (e: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: e,
    }));
  };

  // Handles tab click to filter doctors
  const handleTabClick = (tabName: string) => {
    if (tabName === "pending") {
      dispatch(updateIndicators({ newDoctorRegistered: 0 }));
    }
    setSelectedRows([]);
    setAllDoctors([]);
    setQueryParams((prev) => ({
      ...prev,
      status: tabName,
      page: 1,
    }));
  };

  // Closes the decline modal
  const closeModal = () => {
    setDeclineId("");
    setMessage("");
  };

  /**
   * Handles the decline action with reason
   *
   * API_CALL: POST /doctors/decline/${declineId}
   */
  const handleDecline = async () => {
    if (!message) {
      toast("Please enter reason!", { type: "error" });
      return;
    }
    setDeclineLoader(true);
    const { res, err } = await httpRequest<{ message: string }>({
      method: "post",
      path: `/doctors/decline/${declineId}`,
      params: { message },
    });
    if (res) {
      toast(res.message, { type: "success" });
      setQueryParams((prev) => ({ ...prev, count: !prev.count }));
      closeModal();
      dispatch(getIndicators("/operators/admin-indicators"));
    } else {
      toast(err, { type: "error" });
    }
    setDeclineLoader(false);
  };

  /**
   * Toggles the status of a doctor account
   *
   * API_CALL: POST /doctors/account/status/${id}
   */
  const handleStatusToogle = async (
    id: string,
    status: boolean,
    message?: string
  ) => {
    if (!hasWriteAccess) {
      return;
    }
    if (!status && !message) {
      toast("Please enter reason!", { type: "error" });
      return;
    }
    setStatusId(id);
    if (message) {
      setDeclineLoader(true);
    }
    setLoader((prev) => ({ ...prev, [id]: true }));
    // Build the params object conditionally
    const params: { status: boolean; message?: string } = { status };
    if (message) {
      params.message = message;
    }
    const { res, err } = await httpRequest<{ message: string; data: boolean }>({
      method: "post",
      path: `/doctors/account/status/${id}`,
      params,
    });
    if (res) {
      toast(res.message, { type: "success" });
      setAllDoctors((prev) =>
        prev.map((v) => (v._id === id ? { ...v, accountActive: res.data } : v))
      );
      closeModal();
    } else {
      toast(err, { type: "error" });
    }
    setStatusId("");
    if (message) {
      setDeclineLoader(false);
    }
    setLoader((prev) => ({ ...prev, [id]: false }));
  };

  /**
   * Handles the approval of a doctor
   *
   * API_CALL: POST /doctors/update-status/${id}
   */
  const handleApproved = async (id: string) => {
    setApprovedId(id);
    const { res, err } = await httpRequest<{ message: string }>({
      method: "post",
      path: `/doctors/update-status/${id}`,
      params: {},
    });
    if (res) {
      toast(res.message, { type: "success" });
      handleTabClick("approved");
      dispatch(getIndicators("/operators/admin-indicators"));
    } else {
      toast(err, { type: "error" });
    }
    setApprovedId("");
  };

  // 13/12/2024
  const [emailModal, setEmailModal] = useState<boolean>(false);
  const [postLoader, setPostLoader] = useState<boolean>(false);
  const [errorField, setErrorField] = useState(initialErroState);
  const [payload, setPayload] = useState<IPayloadState>(initialState);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [loader, setLoader] = useState<{
    [key: string]: boolean;
  }>({});
  const handleChange = (key: keyof IPayloadState, val: string) => {
    setPayload((prev) => ({ ...prev, [key]: val }));
    if (val) {
      setErrorField((prev) => ({ ...prev, [key]: "" }));
    }
  };
  const onClose = () => {
    setEmailModal(false);
    setSelectedRows([]);
    setErrorField(initialErroState);
    setPayload(initialState);
  };
  const handleSelectAll = () => {
    setSelectedRows(allDoctors.map((v) => v._id));
  };
  const onToogleSelect = (id: string) => {
    if (selectedRows.includes(id)) {
      // if is exist
      setSelectedRows((prev) => prev.filter((v) => v !== id));
    } else {
      setSelectedRows((prev) => [...prev, id]);
    }
  };
  const validateFields = () => {
    const errors: typeof errorField = {
      subject: payload?.subject ? "" : "Subject is required.",
      title: payload.title ? "" : "Title is required.",
      descripiton: payload.descripiton ? "" : "Description is required.",
    };
    setErrorField(errors);
    // Check if there are any errors
    return !Object.values(errors).some((error) => error !== "");
  };
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (validateFields()) {
      setPostLoader(true);
      const { res, err } = await httpRequest<{ data: string }>({
        method: "post",
        path: "/customer/sendemail",
        params: { ...payload, customers: selectedRows },
      });
      if (res) {
        toast(res?.data || "Success!", { type: "success" });
        onClose();
      } else {
        toast(err, { type: "error" });
      }
      setPostLoader(false);
    }
  };
  // 13/12/2024

  /**
   * Fetches the list of doctors
   *
   * API_CALL: GET /doctors
   */
  const fetchDoctors = async () => {
    setIsLoader(true);
    const { count, ...apiParams } = queryParams;
    const { res, err } = await httpRequest<IDoctorResponse>({
      path: `/doctors?${qs.stringify(apiParams)}`,
    });
    if (res) {
      let { documents = [], paginated } = res;
      setAllDoctors(documents);
      setPaginated(paginated);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    fetchDoctors();
    // dispatch(updateIndicators({ doctor_indicators: false }));
  }, [queryParams]);

  const BackButton: React.FC = () => {
    return (
      <div className="mt-2 flex justify-center">
        <PrimaryBtn
          onPress={() =>
            queryParams.status === "approved"
              ? handleStatusToogle(declineId, false, message)
              : handleDecline()
          }
          type="button"
          loader={declineLoader}
          ButtonText="Send"
          btnExtraStyle={{
            borderRadius: "100rem",
            backgroundColor: "#004540",
          }}
        />
      </div>
    );
  };

  return (
    <>
      <BreadCrumb
        current="Doctors"
        previous="dashboard"
        navigateUrl="/admin"
        btnShow={false}
      />
      <Modal
        open={emailModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="flex items-center flex-col gap-2 bg-[#FFFCF1] w-[600px] p-[20px] rounded-[8px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <button
            onClick={onClose}
            className="absolute top-[-10px] right-[-10px] bg-red-500 rounded-full w-5 h-5 flex justify-center items-center"
          >
            <FontAwesomeIcon
              className="font-size-16px text-white"
              icon={faXmark}
              size="2x"
            />
          </button>
          {/* <img src={modalIcon} alt="icon" width={60} /> */}
          <h1 className="font-size-24px theme-color-green font-Poppins-SemiBold text-center w-[400px] leading-1">
            Compose Email
          </h1>
          <form onSubmit={handleSubmit} className="w-full compose-email-modal">
            <div>
              <SimpleInput
                value={payload.subject}
                onChange={(val) => handleChange("subject", val)}
                type="text"
                placeholder="Subject"
                rows={7}
                extraStyle={{ borderColor: "#009d91" }}
              />
              {errorField?.subject && (
                <p className="font-size-14px text-[#ff0000] font-Poppins-Regular mb-2">
                  {errorField?.subject}
                </p>
              )}
            </div>
            <div>
              <SimpleInput
                value={payload.title}
                onChange={(val) => handleChange("title", val)}
                type="text"
                placeholder="Title"
                rows={7}
                extraStyle={{ borderColor: "#009d91" }}
              />
              {errorField?.title && (
                <p className="font-size-14px text-[#ff0000] font-Poppins-Regular mb-2">
                  {errorField?.title}
                </p>
              )}
            </div>
            <div>
              <SimpleInput
                value={payload.descripiton}
                onChange={(val) => handleChange("descripiton", val)}
                type="textArea"
                placeholder="Description"
                rows={7}
                extraStyle={{ borderColor: "#009d91" }}
              />
              {errorField?.descripiton && (
                <p className="font-size-14px text-[#ff0000] font-Poppins-Regular mb-2">
                  {errorField?.descripiton}
                </p>
              )}
            </div>
            <div className="flex justify-end">
              <PrimaryBtn
                loader={postLoader}
                type="submit"
                ButtonText="Send"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "1.5rem",
                  padding: "0.5rem 2rem",
                }}
              />
            </div>
          </form>
        </div>
      </Modal>
      <div className="all-doctors">
        <DeclineModalReason
          title={`Please specify the reason of deactivation`}
          value={message}
          setValue={setMessage}
          isOpen={declineId ? true : false}
          onClose={closeModal}
          ButtonComponent={BackButton}
        />
        <div className="table-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="products-tabs-btn">
              {tabList.map((v: string) => (
                <button
                  key={v}
                  className={`font-size-15px font-Poppins-Medium relative ${
                    queryParams.status === v && "active"
                  }`}
                  onClick={() => handleTabClick(v)}
                >
                  {v === "approved"
                    ? `Approved Doctors`
                    : v === "pending"
                    ? `Pending Doctors`
                    : ""}
                  {allIndicators?.doctor_indicators && v === "pending" ? (
                    <div className="red-dot-show absolute -top-2 -right-1">
                      <p className="font-Poppins-SemiBold text-white opacity-0 font-size-11px">
                        {allIndicators?.doctor_indicators || 0}
                      </p>
                    </div>
                  ) : allIndicators?.doctor_verfication && v === "approved" ? (
                    <div className="red-dot-show absolute -top-2 -right-1">
                      <p className="font-Poppins-SemiBold text-white opacity-0 font-size-11px">
                        {allIndicators?.doctor_verfication || 0}
                      </p>
                    </div>
                  ) : null}
                </button>
              ))}
            </div>
            <SearchFilterInput
              onChangeText={handleChangeText}
              inputExtraStyle={{ marginBottom: 0, width: 240 }}
            />
            {/* <div className="flex justify-end mb-3"></div> */}
            {/* <div className="flex justify-end">
              <PrimaryBtn
                btnExtralClasses={
                  selectedRows.length ? "" : "cursor-not-allowed"
                }
                disabled={!selectedRows.length}
                title={selectedRows.length ? "" : "Please select customer."}
                ButtonText="Email"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "1.5rem",
                  padding: "0.5rem 2rem",
                }}
                onPress={() => {
                  if (!hasWriteAccess) {
                    return;
                  } else {
                    setEmailModal(true);
                  }
                }}
              />
            </div> */}
          </div>
          <hr
            className="products-tabs-hr products-tabs-hr-allOrder"
            style={{ border: "1px solid #D4D4D4" }}
          />
          <TableWrapper
            isLoading={isLoader}
            page={paginated?.currentPage || 1}
            total={paginated?.totalPages || 0}
            thead={
              queryParams.status !== "approved"
                ? [
                    // CHECKBOX_INPUT,
                    "S.No",
                    "Doctor Name",
                    "Email",
                    "PMDC Number",
                    "Date",
                    "Action",
                  ]
                : [
                    // CHECKBOX_INPUT,
                    "S.No",
                    "Doctor Name",
                    "Email",
                    "PMDC Number",
                    "Status",
                  ]
            }
            onChange={handlePageChange}
            columnWidth={
              queryParams.status === "approved"
                ? "6% 28% 35% 18% 13%"
                : "6% 18% 25% 18% 13% 20%"
            }
            selectedRows={selectedRows}
            totalLength={allDoctors?.length || 1}
            clearAll={() => setSelectedRows([])}
            selectAll={handleSelectAll}
          >
            <div className="tbody-content-height">
              {allDoctors?.map((item: IDoctorItems, index: number) => (
                <ul
                  key={index}
                  className={`font-size-16px tbody-light-table font-Poppins-Regular grid ${
                    !item?.adminNotified && queryParams.status === "pending"
                      ? "font-bold"
                      : !item?.customerVerificationNotified
                      ? "font-bold"
                      : ""
                  }`}
                  style={{
                    gridTemplateColumns:
                      queryParams.status === "approved"
                        ? "6% 28% 35% 18% 13%"
                        : "6% 18% 25% 18% 13% 20%",
                  }}
                  // style={{ gridTemplateColumns: "6% 18% 25% 18% 13% 20%" }}
                >
                  <li>
                    {(
                      (paginated?.currentPage - 1) * queryParams.limit +
                      (index + 1)
                    )
                      .toString()
                      .padStart(2, "0")}
                  </li>
                  {/* <li className={(item?.unRead || item?.unReadNotified) ? "font-bold" : ""}> */}
                  <li>
                    <Link
                      to={`/doctor/${item._id}/View`}
                      className={`${!item?.adminNotified && queryParams.status === "pending" ? "underline" : ""} hover:underline capitalize`}
                      id={`doctors-view-btn-${index + 1}`}
                    >
                      {`${item.firstName} ${item.lastName}`}
                    </Link>
                  </li>
                  <li className="w-full">
                    <span className="truncate w-full block">{item.email}</span>
                  </li>
                  <li>{item.license || item.licenseNumber}</li>
                  {queryParams.status === "approved" ? (
                    <li
                      className="justify-center"
                      style={{ justifyContent: "center", paddingLeft: 0 }}
                    >
                      {item?.isDeleted ? (
                        <div className="font-size-14px font-Poppins-Regular bg-[#ff00003b] text-[red] rounded-[1.5rem] py-1 px-3">
                          Deleted
                        </div>
                      ) : (
                        <div
                          className={`${!hasWriteAccess ? "read_access" : ""}`}
                        >
                          <ToggleRadioBtn
                            id={`doctors-toggle-btn-${index + 1}`}
                            isToggled={item.accountActive}
                            handleToggle={() => {
                              if (!hasWriteAccess) {
                                return;
                              }
                              item.accountActive
                                ? setDeclineId(item._id)
                                : handleStatusToogle(item._id, true);
                            }}
                            loader={loader[item._id] ? true : false}
                          />
                        </div>
                      )}
                    </li>
                  ) : (
                    <li>{moment(item.createdAt).format("DD-MM-YYYY")}</li>
                  )}
                  {queryParams.status !== "approved" && (
                    <li className="justify-center gap-2">
                      {queryParams.status === "approved" ? (
                        <>
                          <Link
                            id={`doctors-health-profile-btn-${index + 1}`}
                            to={
                              !hasWriteAccess
                                ? "#"
                                : `/health-profile/${
                                    item._id
                                  }/View?isdoctor=true&name=${`${item.firstName} ${item.lastName}`}`
                            }
                          >
                            <img
                              src={eyeIcon}
                              alt="eye"
                              width="18"
                              className={`${
                                !hasWriteAccess ? "read_access" : ""
                              }`}
                            />
                          </Link>
                          <Link
                            id={`doctors-view-test-btn-${index + 1}`}
                            to={
                              !hasWriteAccess
                                ? "#"
                                : `/view/${item._id}/all-test?isdoctor=true`
                            }
                          >
                            <img
                              src={viewTest}
                              alt="view-test"
                              width="18"
                              className={`${
                                !hasWriteAccess ? "read_access" : ""
                              }`}
                            />
                          </Link>
                          <Link
                            className="relative"
                            id={`doctors-add-test-btn-${index + 1}`}
                            to={
                              !hasWriteAccess
                                ? "#"
                                : `/add-result/${item._id}/View?isdoctor=true`
                            }
                          >
                            <img
                              src={addTest}
                              alt="add-test"
                              width="18"
                              className={`${
                                !hasWriteAccess ? "read_access" : ""
                              }`}
                            />
                            {!item?.diagnosticTestNotified && (
                              <div className="red-dot-show absolute red-dot-show-position-set"></div>
                            )}
                          </Link>
                        </>
                      ) : (
                        <>
                          <button
                            className={`font-size-16px font-Poppins-SemiBold flex items-center gap-2 justify-center ${
                              !hasWriteAccess ? "cursor-not-allowed" : ""
                            }`}
                            style={greenBtn}
                            onClick={() => {
                              if (!hasWriteAccess) {
                                return;
                              } else {
                                handleApproved(item._id);
                              }
                            }}
                            id={`doctors-approve-btn-${index + 1}`}
                          >
                            {approvedId === item._id ? (
                              <CircularProgress
                                size={14}
                                style={{ color: "#00443f" }}
                              />
                            ) : (
                              "Approve"
                            )}
                          </button>
                          <button
                            className={`font-size-16px font-Poppins-SemiBold ${
                              !hasWriteAccess ? "cursor-not-allowed" : ""
                            }`}
                            style={redBtn}
                            onClick={() => {
                              if (!hasWriteAccess) {
                                return;
                              } else {
                                setDeclineId(item._id);
                              }
                            }}
                            id={`doctors-decline-btn-${index + 1}`}
                          >
                            Decline
                          </button>
                        </>
                      )}
                    </li>
                  )}
                </ul>
              ))}
            </div>
          </TableWrapper>
        </div>
      </div>
    </>
  );
};

export default Doctors;
